/**
 * CSV import destination fields for CVIN expansion (subsegment) projects.
 */
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { EXPANSION_ACF_GROUPS } from "./expansionProjectAcf";
import { MATERIALS_JSON_FIELD_ID, MATERIALS_JSON_HEADER_ALIASES } from "./materialsJsonImport";
import { PERMITS_JSON_FIELD_ID, PERMITS_JSON_HEADER_ALIASES } from "./permitsJsonImport";

function field(id, label, group, fieldType = "text") {
  return { id, label, group, fieldType };
}

const SUB = EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY;
const SUMMARY = EXPANSION_ACF_GROUPS.SUMMARY;
const STATS = EXPANSION_ACF_GROUPS.STATS;
const LEGACY_SUB = EXPANSION_ACF_GROUPS.SUBSEGMENTS;
const ENG = COMMERCIAL_DOMAINS.ENGINEERING;
const CON = COMMERCIAL_DOMAINS.CONSTRUCTION;
const FIB = COMMERCIAL_DOMAINS.FIBER;
const ACCT = COMMERCIAL_DOMAINS.ACCOUNTING;

export const EXPANSION_CSV_IMPORT_FIELDS = [
  field(`${SUB}.work_order`, "Subsegment summary → Work order", "match"),
  field(`${LEGACY_SUB}.work_order`, "Legacy subsegment → Work order", "match"),
  field(`${SUMMARY}.work_order`, "Summary → Work order", "match"),
  field(`${SUB}.segment`, "Subsegment summary → Segment", "subsegment"),
  field(`${SUB}.project_name`, "Subsegment summary → Project name", "subsegment"),
  field(`${SUB}.status`, "Subsegment summary → Status", "subsegment"),
  field(`${SUB}.phase`, "Subsegment summary → Phase", "subsegment"),
  field(`${SUB}.subphase`, "Subsegment summary → Subphase", "subsegment"),
  field(`${SUB}.departments`, "Subsegment summary → Departments", "subsegment"),
  field(`${SUB}.bid_out`, "Subsegment summary → Bid out", "subsegment", "bool"),
  field(`${SUB}.construction_footage`, "Subsegment summary → Construction footage", "subsegment", "number"),
  field(`${SUB}.override_footage`, "Subsegment summary → Override footage", "subsegment", "number"),
  field(`${SUB}.caltrans_footage`, "Subsegment summary → Caltrans footage", "subsegment", "number"),
  field(`${SUB}.handholes`, "Subsegment summary → Handholes", "subsegment", "number"),
  field(`${SUB}.subphases.drafting_subphase`, "Subphase → Drafting", "subphases"),
  field(`${SUB}.subphases.permitting_subphase`, "Subphase → Permitting", "subphases"),
  field(`${SUB}.subphases.construction_subphase`, "Subphase → Construction", "subphases"),
  field(`${SUB}.subphases.fiber_subphase`, "Subphase → Fiber", "subphases"),
  field(`${SUB}.subphases.accounting_subphase`, "Subphase → Accounting", "subphases"),
  field(`${LEGACY_SUB}.segment_number`, "Legacy → Segment number", "subsegment", "number"),
  field(`${LEGACY_SUB}.segment_name`, "Legacy → Segment name", "subsegment"),
  field(`${SUMMARY}.aecom_id`, "Summary → AECOM ID", "summary"),
  field(`${SUMMARY}.customer_name`, "Summary → Customer", "summary"),
  field(`${SUMMARY}.site_name`, "Summary → Site / project name", "summary"),
  field(`${SUMMARY}.phase_exp`, "Summary → Phase [Expansion] (legacy)", "summary"),
  field(`${SUMMARY}.work_order_type`, "Summary → Work order type", "summary"),
  field(`${SUMMARY}.incident`, "Summary → Incident work order", "summary", "bool"),
  field(`${SUMMARY}.cvin_expansion`, "Summary → CVIN expansion flag", "summary", "bool"),
  field(`${STATS}.construction_footage`, "Segment stats → Construction footage (legacy)", "statistics", "number"),
  field(`${STATS}.override_footage`, "Segment stats → Override footage (legacy)", "statistics", "number"),
  field(`${STATS}.caltrans_footage`, "Segment stats → Caltrans footage (legacy)", "statistics", "number"),
  field(`${STATS}.construction_in_ct_row`, "Segment stats → Footage in CT ROW (legacy)", "statistics", "number"),
  field(`${STATS}.handholes`, "Segment stats → Handholes (legacy)", "statistics", "number"),
  field(`${STATS}.fiber_count`, "Segment stats → Fiber count", "statistics"),
  field(`${STATS}.waterway`, "Segment stats → Waterway", "statistics", "bool"),
  field(`${ENG}.drafter`, "Engineering → Drafter", "engineering"),
  field(`${ENG}.preliminary_route`, "Engineering → Route (.kmz/.kml)", "engineering"),
  field(PERMITS_JSON_FIELD_ID, "Engineering → Permits (JSON)", "engineering", "permits_json"),
  field(MATERIALS_JSON_FIELD_ID, "Engineering → Material totals (JSON)", "engineering", "materials_json"),
  field(`${CON}.inspector`, "Construction → Inspector", "construction"),
  field(`${CON}.construction_contractor`, "Construction → Contractor", "construction"),
  field(`${CON}.contract_cost`, "Construction → Contract cost", "construction", "number"),
  field(`${CON}.contract_execution_date`, "Construction → Contract execution date", "construction", "date"),
  field(`${CON}.contractor_start_date`, "Construction → Contractor start date", "construction", "date"),
  field(`${CON}.contract_expiration_date`, "Construction → Contract expiration date", "construction", "date"),
  field(`${CON}.fiber_contractor`, "Construction → Fiber contractor", "construction"),
  field(`${FIB}.splicer`, "Fiber → Splicer", "fiber"),
  field(`${ACCT}.completion_notice`, "Accounting → Completion notice", "accounting"),
  field(`${ACCT}.nrc`, "Accounting → NRC", "accounting", "number"),
  field(`${ACCT}.term`, "Accounting → Term", "accounting", "number"),
  field(`${ACCT}.invoiced`, "Accounting → Invoiced", "accounting"),
  field(`${ACCT}.accounting_contact`, "Accounting → Contact", "accounting"),
  field(`${ACCT}.accounting_number`, "Accounting → Phone", "accounting"),
  field(`${ACCT}.accounting_email`, "Accounting → Email", "accounting"),
];

export const EXPANSION_CSV_MATCH_FIELDS = [
  {
    id: `${SUB}.work_order`,
    label: "Subsegment work order (e.g. 6784.07)",
    hint: "Primary match for expansion subsegments",
  },
  {
    id: `${LEGACY_SUB}.work_order`,
    label: "Legacy subsegment work order",
    hint: "Match when CSV uses expansion_subsegments.work_order",
  },
  {
    id: `${SUMMARY}.work_order`,
    label: "Summary work order",
    hint: "Match when CSV uses the summary WO field",
  },
  {
    id: "export_slug",
    label: "Export slug",
    hint: "Optional legacy slug match (e.g. seg-6784-07)",
  },
];

export const EXPANSION_CSV_DESTINATION_FIELDS = [
  { id: "", label: "— Do not import —", group: "meta" },
  ...EXPANSION_CSV_MATCH_FIELDS.map((f) => ({ ...f, group: "match" })),
  ...EXPANSION_CSV_IMPORT_FIELDS.filter(
    (f) => !EXPANSION_CSV_MATCH_FIELDS.some((m) => m.id === f.id)
  ),
];

const FIELD_ALIAS_MAP = {
  [`${SUB}.work_order`]: ["work_order", "workorder", "wo", "subsegment_work_order", "sub_wo"],
  [`${LEGACY_SUB}.work_order`]: ["expansion_subsegments_work_order"],
  [`${SUMMARY}.work_order`]: ["summary_work_order", "parent_work_order"],
  [`${SUB}.segment`]: ["segment", "segment_number", "segment_no", "seg_number"],
  [`${SUB}.project_name`]: ["project_name", "segment_name", "name", "project", "title", "subsegment_name"],
  [`${SUB}.status`]: ["status"],
  [`${SUB}.phase`]: ["phase", "phase_exp", "expansion_phase"],
  [`${SUB}.subphase`]: ["subphase", "sub_phase"],
  [`${SUB}.departments`]: ["departments", "department_involvement"],
  [`${SUB}.bid_out`]: ["bid_out", "bidout"],
  [`${SUB}.construction_footage`]: ["construction_footage", "footage", "const_footage"],
  [`${SUB}.override_footage`]: ["override_footage", "override"],
  [`${SUB}.caltrans_footage`]: ["caltrans_footage", "caltrans", "construction_in_ct_row", "ct_row_footage"],
  [`${SUB}.handholes`]: ["handholes", "hh"],
  [`${SUB}.subphases.drafting_subphase`]: ["drafting_subphase", "drafting", "drafting_status"],
  [`${SUB}.subphases.permitting_subphase`]: ["permitting_subphase", "permitting", "permitting_status"],
  [`${SUB}.subphases.construction_subphase`]: ["construction_subphase", "construction_status"],
  [`${SUB}.subphases.fiber_subphase`]: ["fiber_subphase", "fiber_status", "splicing"],
  [`${SUB}.subphases.accounting_subphase`]: ["accounting_subphase", "accounting_status"],
  [`${LEGACY_SUB}.segment_number`]: ["legacy_segment_number"],
  [`${LEGACY_SUB}.segment_name`]: ["legacy_segment_name"],
  [`${SUMMARY}.aecom_id`]: ["aecom_id", "aecom", "aecomid"],
  [`${SUMMARY}.customer_name`]: ["customer", "customer_name", "client"],
  [`${SUMMARY}.site_name`]: ["site_name", "site"],
  [`${SUMMARY}.phase_exp`]: ["legacy_phase_exp"],
  [`${SUMMARY}.work_order_type`]: ["work_order_type", "wo_type", "type"],
  [`${SUMMARY}.incident`]: ["incident"],
  [`${STATS}.construction_footage`]: ["legacy_construction_footage"],
  [`${STATS}.override_footage`]: ["legacy_override_footage"],
  [`${STATS}.caltrans_footage`]: ["legacy_caltrans_footage"],
  [`${STATS}.construction_in_ct_row`]: ["legacy_ct_row"],
  [`${STATS}.handholes`]: ["legacy_handholes"],
  [`${STATS}.fiber_count`]: ["fiber_count", "fiber_ct"],
  [`${STATS}.waterway`]: ["waterway"],
  [`${ENG}.drafter`]: ["drafter"],
  [`${ENG}.preliminary_route`]: ["preliminary_route", "route", "route_file"],
  [PERMITS_JSON_FIELD_ID]: PERMITS_JSON_HEADER_ALIASES,
  [MATERIALS_JSON_FIELD_ID]: MATERIALS_JSON_HEADER_ALIASES,
  [`${CON}.inspector`]: ["inspector"],
  [`${CON}.construction_contractor`]: ["construction_contractor", "contractor"],
  [`${CON}.contract_cost`]: ["contract_cost", "contractor_cost", "contract_cost_usd"],
  [`${CON}.contract_execution_date`]: ["contract_execution_date", "contract_execution"],
  [`${CON}.contractor_start_date`]: ["contractor_start_date", "contractor_start"],
  [`${CON}.contract_expiration_date`]: [
    "contract_expiration_date",
    "contract_expiration",
    "contracted_completion_date",
  ],
  [`${CON}.fiber_contractor`]: ["fiber_contractor", "contractor_fiber"],
  [`${FIB}.splicer`]: ["splicer"],
  [`${ACCT}.completion_notice`]: ["completion_notice"],
  [`${ACCT}.nrc`]: ["nrc"],
  [`${ACCT}.term`]: ["term"],
  [`${ACCT}.invoiced`]: ["invoiced"],
  [`${ACCT}.accounting_contact`]: ["accounting_contact"],
  [`${ACCT}.accounting_number`]: ["accounting_number", "accounting_phone"],
  [`${ACCT}.accounting_email`]: ["accounting_email"],
  export_slug: ["export_slug", "slug"],
};

export const EXPANSION_CSV_BOOL_FIELDS = new Set(
  EXPANSION_CSV_IMPORT_FIELDS.filter((f) => f.fieldType === "bool").map((f) => f.id)
);

export const EXPANSION_CSV_NUM_FIELDS = new Set(
  EXPANSION_CSV_IMPORT_FIELDS.filter((f) => f.fieldType === "number").map((f) => f.id)
);

export const EXPANSION_CSV_PERMITS_JSON_FIELDS = new Set(
  EXPANSION_CSV_IMPORT_FIELDS.filter((f) => f.fieldType === "permits_json").map((f) => f.id)
);

export const EXPANSION_CSV_MATERIALS_JSON_FIELDS = new Set(
  EXPANSION_CSV_IMPORT_FIELDS.filter((f) => f.fieldType === "materials_json").map((f) => f.id)
);

export const EXPANSION_CSV_DESTINATION_GROUP_ORDER = [
  "match",
  "subsegment",
  "summary",
  "subphases",
  "statistics",
  "engineering",
  "construction",
  "fiber",
  "accounting",
];

function normalizeHeaderKey(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function suggestExpansionDestinationForCsvColumn(csvHeader) {
  const n = normalizeHeaderKey(csvHeader);
  if (!n) return "";
  if (FIELD_ALIAS_MAP.export_slug?.includes(n)) return "export_slug";
  for (const [fieldId, aliases] of Object.entries(FIELD_ALIAS_MAP)) {
    if (fieldId === "export_slug") continue;
    if (aliases.includes(n) || n === fieldId.replace(/\./g, "_")) return fieldId;
  }
  for (const f of EXPANSION_CSV_DESTINATION_FIELDS) {
    if (!f.id) continue;
    const shortKey = f.id.includes(".") ? f.id.split(".").pop() : f.id;
    if (n === normalizeHeaderKey(shortKey)) return f.id;
  }
  return "";
}

export function buildExpansionAutoColumnMapping(headers) {
  const mapping = {};
  const used = new Set();
  for (const h of headers) {
    let dest = suggestExpansionDestinationForCsvColumn(h);
    if (dest && used.has(dest)) dest = "";
    if (dest) used.add(dest);
    mapping[h] = dest;
  }
  return mapping;
}

export function invertExpansionMapping(columnToDest) {
  const destToCol = {};
  for (const [col, dest] of Object.entries(columnToDest)) {
    if (!dest || destToCol[dest]) continue;
    destToCol[dest] = col;
  }
  return destToCol;
}

export function groupLabelForExpansionImportField(field) {
  const labels = {
    match: "Match key",
    subsegment: "Subsegment summary",
    summary: "Project summary (expansion)",
    subphases: "Expansion subphases",
    statistics: "Expansion segment statistics (legacy)",
    engineering: "Engineering details",
    construction: "Construction details",
    fiber: "Fiber details",
    accounting: "Accounting details",
  };
  return labels[field.group] || field.group;
}

export { invertExpansionMapping as invertMapping };
